import { Outlet } from "react-router";
import { useEffect, useMemo, lazy, Suspense, Component, type ReactNode } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { ContactFormSection } from "../shared/ContactFormSection";
import { ContentEditingProvider, useContentEditingSafe } from "../shared/ContentEditingContext";
import { ContentEditToggle } from "../shared/ContentEditToggle";
const GlobalContentEditor = lazy(() =>
  import("../shared/GlobalContentEditor").then(m => ({ default: m.GlobalContentEditor }))
);
import { ContentBlockZone } from "../shared/ContentBlocks";
import { Breadcrumbs } from "../shared/Breadcrumbs";
import { CommentSystem } from "../shared/CommentSystem";
const BlockPalette = lazy(() =>
  import("../shared/BlockPalette").then(m => ({ default: m.BlockPalette }))
);
import { SeoHead } from "../shared/SeoHead";
import { AdminBar } from "../shared/AdminBar";
import { CategoryBar } from "../shared/CategoryBar";
import { BackToTop } from "../shared/BackToTop";
import { AutoSaveIndicator } from "../shared/AutoSaveIndicator";
import { PageSkeletonOverlay } from "../shared/PageSkeletonOverlay";
import { ResponsivePreviewFrame } from "../shared/ResponsivePreviewFrame";
import { CartProvider } from "../shared/CartContext";
import { CartDrawer } from "../shared/CartDrawer";
import { useRelativePath, useTemplateType } from "../shared/TemplateContext";
import { breadcrumbs as brandingBreadcrumbs, layout as brandingLayout } from "../../siteConfig";
import { breadcrumbs as storeBreadcrumbs, layout as storeLayout, getCategory } from "../../storeSiteConfig";
import { findPostBySlug } from "../../data/blogData";
import { CommandPalette } from "../shared/CommandPalette";
import { HelpDrawer } from "../shared/HelpDrawer";
import { KeyboardShortcutsModal } from "../shared/KeyboardShortcutsModal";

/* ================================================================
   Tiny Error Boundary — prevents a crash inside the main content
   from killing the entire layout (especially the floating buttons).
   Includes a retry button and detailed error info.
   ================================================================ */

interface EBProps { children: ReactNode; name?: string }
interface EBState { hasError: boolean; error: string; errorCount: number }

class SafeZone extends Component<EBProps, EBState> {
  state: EBState = { hasError: false, error: "", errorCount: 0 };
  static getDerivedStateFromError(err: Error): Partial<EBState> {
    return { hasError: true, error: String(err) };
  }
  componentDidCatch(err: Error, info: React.ErrorInfo) {
    console.error(`[SafeZone:${this.props.name ?? "?"}]`, err, info);
    this.setState((prev) => ({ errorCount: prev.errorCount + 1 }));
  }
  handleRetry = () => {
    this.setState({ hasError: false, error: "" });
  };
  render() {
    if (this.state.hasError) {
      return (
        <div dir="rtl" style={{ padding: 32, textAlign: "center" }}>
          <div style={{ maxWidth: 480, margin: "0 auto", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 24 }}>
            <p style={{ fontWeight: 700, fontSize: 18, color: "#b91c1c", marginBottom: 8 }}>{"\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E8\u05E0\u05D3\u05D5\u05E8"}</p>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>{"\u05D0\u05D9\u05E8\u05E2\u05D4 \u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E2\u05EA \u05D4\u05E6\u05D2\u05EA \u05D4\u05E2\u05DE\u05D5\u05D3. \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1 \u05D0\u05D5 \u05E8\u05E2\u05E0\u05DF \u05D0\u05EA \u05D4\u05D3\u05E3."}</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }}>
              <button
                type="button"
                onClick={this.handleRetry}
                style={{
                  padding: "8px 20px",
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {"\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  padding: "8px 20px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {"\u05E8\u05E2\u05E0\u05DF \u05D3\u05E3"}
              </button>
            </div>
            <details style={{ textAlign: "left" }}>
              <summary style={{ fontSize: 11, color: "#9ca3af", cursor: "pointer" }}>Error details</summary>
              <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", marginTop: 8, color: "#6b7280", background: "#f9fafb", padding: 8, borderRadius: 6, maxHeight: 200, overflow: "auto" }}>
                {this.state.error}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ================================================================ */

/**
 * Wraps <main> so it can read isLoading from ContentEditingContext.
 * Uses visibility:hidden (not display:none) to preserve layout space
 * while suppressing paint — prevents CLS from placeholder→real content swap.
 * PageSkeletonOverlay covers the full viewport on top; this is the backstop.
 */
function MainContent({ showBlockZone }: { showBlockZone: boolean }) {
  const ctx = useContentEditingSafe();
  const isLoading = ctx?.isLoading ?? false;
  return (
    <main
      className="flex-1"
      style={isLoading ? { visibility: "hidden", minHeight: "60vh" } : undefined}
    >
      <Outlet />
      {showBlockZone && <ContentBlockZone zoneId="bottom" />}
    </main>
  );
}

export function PageLayout() {
  const relativePath = useRelativePath();
  const templateType = useTemplateType();

  const isStore = templateType === "store";
  const isFacelift = templateType === "facelift";
  const layout = isStore ? storeLayout : brandingLayout;
  const breadcrumbConfig = isStore ? storeBreadcrumbs : brandingBreadcrumbs;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [relativePath]);

  const isHomePage = relativePath === layout.homePath;
  const hideContactForm = layout.hideContactFormOn.includes(relativePath);
  const hideFooter = relativePath === "/qa-checklist";
  const hideEditingTools = relativePath === "/qa-checklist";

  // Build breadcrumbs — static config + dynamic blog/catalog pages
  const breadcrumbItems = useMemo(() => {
    // Check static config first
    if (breadcrumbConfig[relativePath]) return breadcrumbConfig[relativePath];

    // Dynamic: blog post pages (/blog/:slug)
    const blogMatch = relativePath.match(/^\/blog\/(.+)$/);
    if (blogMatch) {
      const slug = blogMatch[1];
      const post = findPostBySlug(slug);
      return [
        { label: "\u05D1\u05DC\u05D5\u05D2", path: "/blog" },
        { label: post?.title ?? "\u05DE\u05D0\u05DE\u05E8" },
      ];
    }

    // Dynamic: catalog category pages (/catalog/:categorySlug)
    if (isStore) {
      const catMatch = relativePath.match(/^\/catalog\/([^/]+)$/);
      if (catMatch) {
        const cat = getCategory(catMatch[1]);
        return [
          { label: "\u05E7\u05D8\u05DC\u05D5\u05D2", path: "/catalog" },
          { label: cat?.name ?? catMatch[1] },
        ];
      }

      // Dynamic: product pages (/catalog/:categorySlug/:productSlug)
      const prodMatch = relativePath.match(/^\/catalog\/([^/]+)\/([^/]+)$/);
      if (prodMatch) {
        const cat = getCategory(prodMatch[1]);
        return [
          { label: "\u05E7\u05D8\u05DC\u05D5\u05D2", path: "/catalog" },
          { label: cat?.name ?? prodMatch[1], path: `/catalog/${prodMatch[1]}` },
          { label: "\u05DE\u05D5\u05E6\u05E8" },
        ];
      }
    }

    return undefined;
  }, [relativePath, breadcrumbConfig, isStore]);

  /* ── Inner content: DnD + Editing providers + page chrome ── */
  const innerContent = (
    <SafeZone name="main-content">
      <DndProvider backend={HTML5Backend}>
        <ContentEditingProvider>
          <CommentSystem>
            <ResponsivePreviewFrame>
              <div dir="rtl" className="min-h-screen flex flex-col" style={{ fontFamily: layout.fontFamily }}>
                <AdminBar />
                {!isFacelift && <Header />}
                {isStore && <CategoryBar />}
                {!isHomePage && breadcrumbItems && (
                  <Breadcrumbs items={breadcrumbItems} />
                )}
                <MainContent showBlockZone={!isFacelift} />
                {!hideContactForm && !isFacelift && <ContactFormSection />}
                {!isFacelift && !hideFooter && <Footer />}
                <BackToTop />
                <AutoSaveIndicator />
              </div>
            </ResponsivePreviewFrame>
          </CommentSystem>
          <PageSkeletonOverlay />
          {!hideEditingTools && <ContentEditToggle />}
          <Suspense fallback={null}>
            <GlobalContentEditor />
          </Suspense>
          <Suspense fallback={null}>
            <BlockPalette />
          </Suspense>
          <SeoHead />
          <CommandPalette />
          {/* Cart drawer for store templates */}
          {isStore && <CartDrawer />}
        </ContentEditingProvider>
      </DndProvider>
    </SafeZone>
  );

  /* ── HelpDrawer lives OUTSIDE all providers so it always renders ── */
  const helpDrawer = (
    <SafeZone name="help-drawer">
      <HelpDrawer />
      <KeyboardShortcutsModal />
    </SafeZone>
  );

  // Wrap store templates with CartProvider
  if (isStore) {
    return (
      <>
        <CartProvider>{innerContent}</CartProvider>
        {helpDrawer}
      </>
    );
  }

  return (
    <>
      {innerContent}
      {helpDrawer}
    </>
  );
}